import type { IUiThreeDPresentation } from './Interfaces';
import * as THREE from 'three';
import type { IImageSliceViewModel } from '@/engine/app/interfaces';
import type { IPresentationChangeHandler } from './Interfaces';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { SlicePositionInfo, SliceOrientationInfo } from '@/engine/app/modules/images/ImageStackController';
import type { IImagesPortContext, SliceType } from '@/engine/app/modules/images/interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { XRayEffect } from '../app/modules/images/ImageStack/ImageStack';

export class ImageBorderPresentation implements IUiThreeDPresentation {
  private _planeGeometry: THREE.BufferGeometry;

  private _planeMaterial: THREE.MeshBasicMaterial;

  private _xRayPlaneMaterial: THREE.MeshBasicMaterial;

  private _planeMesh: THREE.Mesh;

  private _xRayPlaneMesh1: THREE.Mesh;

  private _xRayPlaneMesh2: THREE.Mesh;

  private _threeDObject: THREE.Object3D;

  private _imagesService: ImagesService;

  private _sliceType: SliceType;

  private _sliceOrientationInfo: SliceOrientationInfo | undefined;

  private _changeHandler: IPresentationChangeHandler;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private _imagesPortContext: IImagesPortContext;

  private _sliceCS: THREE.Matrix4 | undefined;

  private _presentationCS: THREE.Matrix4 | undefined;

  public constructor(
    imagesService: ImagesService,
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler,
    imagesPortContext: IImagesPortContext
  ) {
    this._imagesService = imagesService;
    this._sliceType = imageModel.descriptor;

    this._planeGeometry = new THREE.PlaneGeometry();

    this._planeMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(imageModel.color),
      wireframe: true,
      depthTest: false
    });
    this._xRayPlaneMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(imageModel.color).lerp(new THREE.Color('#FFFFFF'), 0.3),
      wireframe: true,
      depthTest: false
    });

    this._planeMesh = new THREE.Mesh(this._planeGeometry, this._planeMaterial);
    this._planeMesh.matrixAutoUpdate = false;
    this._planeMesh.renderOrder = 0;
    this._planeMesh.visible = true; // permanently visible
    this._planeMesh.name = this._sliceType;

    this._xRayPlaneMesh1 = new THREE.Mesh(this._planeGeometry, this._xRayPlaneMaterial);
    this._xRayPlaneMesh1.matrixAutoUpdate = false;
    this._xRayPlaneMesh1.renderOrder = 0;
    this._xRayPlaneMesh1.visible = false; // visibility is conditional
    this._xRayPlaneMesh1.name = this._sliceType;

    this._xRayPlaneMesh2 = new THREE.Mesh(this._planeGeometry, this._xRayPlaneMaterial);
    this._xRayPlaneMesh2.matrixAutoUpdate = false;
    this._xRayPlaneMesh2.renderOrder = 0;
    this._xRayPlaneMesh2.visible = false; // visibility is conditional
    this._xRayPlaneMesh2.name = this._sliceType;

    this._threeDObject = new THREE.Object3D().add(this._planeMesh).add(this._xRayPlaneMesh1).add(this._xRayPlaneMesh2);

    this._changeHandler = changeHandler;

    this._imagesPortContext = imagesPortContext;

    this._subscriptions.add(
      this._imagesService.subscribeToSliceOrientationChange(
        this._sliceType,
        this._handleSliceOrientationChange.bind(this)
      )
    );
    this._subscriptions.add(
      this._imagesService.subscribeToSlicePositionChange(this._sliceType, this._handleSlicePositionChange.bind(this))
    );
    this._subscriptions.add(
      this._imagesPortContext.addOnSliceCSChangedListener(this._onSliceCSChanged.bind(this), true)
    );
  }

  public get3dObject(): THREE.Object3D {
    return this._threeDObject;
  }

  public dispose(): void {
    this._subscriptions.dispose();
    this._planeMaterial.dispose();
    this._xRayPlaneMaterial.dispose();
    this._planeGeometry.dispose();
  }

  private _handleSliceOrientationChange(orientationInfo: SliceOrientationInfo): void {
    this._sliceOrientationInfo = orientationInfo;

    this._planeGeometry.dispose();
    this._planeGeometry = new THREE.PlaneGeometry(2000, 2000, 1.4, 1.4); //width&height + Xmm - to ensure wireframes are visible
    this._planeMaterial.needsUpdate = true;
    this._xRayPlaneMaterial.needsUpdate = true;

    this._planeMesh.geometry = this._planeGeometry;
    this._xRayPlaneMesh1.geometry = this._planeGeometry;
    this._xRayPlaneMesh2.geometry = this._planeGeometry;
  }

  private _handleSlicePositionChange(positionInfo: SlicePositionInfo): void {
    this._presentationCS = positionInfo.sliceCS.clone();
    this._setMeshPosition(positionInfo);
    this._updateAppearance();
    this._changeHandler.handlePresentationChange();
  }

  private _setMeshPosition(sliceContent: SlicePositionInfo): void {
    if (!this._sliceOrientationInfo) throw new Error('Slice Orientation Info must be obtained by this point');

    const pos = new THREE.Vector3(
      this._sliceOrientationInfo.volumeDimensions.x / 2,
      this._sliceOrientationInfo.volumeDimensions.y / 2,
      0
    ).applyMatrix4(sliceContent.sliceCS);

    this._planeMesh.matrix = sliceContent.sliceCS.clone();
    this._planeMesh.matrix.setPosition(pos);
    this._planeMesh.updateMatrixWorld(true);

    if (sliceContent.xRayEffect === XRayEffect.Addition) {
      const pos1 = new THREE.Vector3(
        this._sliceOrientationInfo.volumeDimensions.x / 2,
        this._sliceOrientationInfo.volumeDimensions.y / 2,
        -sliceContent.sliceThickness / 2
      ).applyMatrix4(sliceContent.sliceCS);

      const pos2 = new THREE.Vector3(
        this._sliceOrientationInfo.volumeDimensions.x / 2,
        this._sliceOrientationInfo.volumeDimensions.y / 2,
        sliceContent.sliceThickness / 2
      ).applyMatrix4(sliceContent.sliceCS);

      this._xRayPlaneMesh1.visible = true;
      this._xRayPlaneMesh1.matrix = sliceContent.sliceCS.clone();
      this._xRayPlaneMesh1.matrix.setPosition(pos1);
      this._xRayPlaneMesh1.updateMatrixWorld(true);

      this._xRayPlaneMesh2.visible = true;
      this._xRayPlaneMesh2.matrix = sliceContent.sliceCS.clone();
      this._xRayPlaneMesh2.matrix.setPosition(pos2);
      this._xRayPlaneMesh2.updateMatrixWorld(true);
    } else {
      this._xRayPlaneMesh1.visible = false;
      this._xRayPlaneMesh2.visible = false;
    }
  }

  private _onSliceCSChanged(sliceCS: THREE.Matrix4): void {
    this._sliceCS = sliceCS;
    this._updateAppearance();
    this._changeHandler.handlePresentationChange();
  }

  private _updateAppearance(): void {
    if (!this._presentationCS || !this._sliceCS) {
      return;
    }
    const sliceVZ = new THREE.Vector3(0, 0, 1).transformDirection(this._sliceCS);
    const presentationVZ = new THREE.Vector3(0, 0, 1).transformDirection(this._presentationCS);
    const isVisible = Math.abs(presentationVZ.angleTo(sliceVZ) - Math.PI / 2) < 0.0001;
    this._threeDObject.visible = isVisible;
  }
}
